import { ProjectWithAssigneesDto } from '../../../commonTypes/acs';
import { AssigneeDto, ProjectAssigneesWithUserInfoDto, EmploymentType } from '../../../commonTypes/project.types';
import { useSelector, useDispatch } from 'react-redux';
import { setProjectAssignees, updateAssignee } from '../../../store/slices/project-assignees.slice';
import { selectAuth } from '../../../store/slices/auth.slice';
import { selectOrganizationMembers } from '../../../store/slices/add-edit-policy.slice';
import {
    invalidateSingleProjectCache,
    invalidateProjectMetrics,
    invalidateTimesheetCache,
    invalidateActivityLogsCache,
} from '../../../store/slices/projects.slice';
import { RootState } from '../../../store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AssignMembersDrawer from '../../AssignMembersDrawer';
import { ActivityFeed } from './ActivityFeed';
import { MetricCard } from '../../Common/MetricCard';
import { OverviewAssignmentSection, type OverviewAssignmentView } from './OverviewAssignmentSection';
import {
    useHttpGetProjectRoles,
    useHttpPostProjectAssignees,
    useHttpGetProjectTotalEarnings,
    useHttpGetProjectAssigneeAssignmentHistory,
    useOrganizationId,
} from '../../../hooks';
import useHttpPatchProjectAssignee from '../../../hooks/project/projectApi/useHttpPatchProjectAssignee';
import useHttpDeleteProjectAssignee from '../../../hooks/project/projectApi/useHttpDeleteProjectAssignee';
import { useHttpGetProjectHoursSummary, useHttpGetUserTimeLogs } from '../../../hooks/timelog/timelogApi';
import useHttpGetMembersOMX from '../../../hooks/user/userApi/useHttpGetMembersOMX';
import { RateType } from '@cyberlabrs/helpers';
import { DateTime } from 'luxon';
import { getCurrencySymbol } from '../../../util/general';
import { formatDateAndTime } from '../../../util/date/dateFormater';
import { checkIfAdminOrOwner } from '../../../api/utils/roleUtils';
import { buildUpdateAssigneeBody } from '../../../util/projectAssigneeRate/effectiveFrom';
import type { AssignMembersSubmitOptions } from '../../AssignMembersDrawer';
import { RoleTypeEnum } from '../../../commonTypes/user.types';
import { useTranslation } from '../../../hooks/useTranslation';

const EMPLOYMENT_TYPES = [
    { label: 'Full Time', value: 'Full-time' },
    { label: 'Part Time', value: 'Part-time' },
    { label: 'Contract', value: 'Contract' },
];

interface ProjectOverviewTabProps {
    project: ProjectWithAssigneesDto;
    onEditMember?: (member: ProjectAssigneesWithUserInfoDto) => void;
    onRefresh?: () => void;
    canEdit?: boolean;
    showRates?: boolean;
    showMetrics?: boolean;
    showOnlyOwnData?: boolean;
    currentUserSub?: string;
    currency?: string;
}

export const ProjectOverviewTab = ({
    project,
    onRefresh,
    canEdit = true,
    showRates = true,
    showMetrics,
    showOnlyOwnData = false,
    currency = 'USD',
}: ProjectOverviewTabProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isAssignMemberDrawerOpen, setIsAssignMemberDrawerOpen] = useState(false);
    const [editingMember, setEditingMember] = useState<ProjectAssigneesWithUserInfoDto | null>(null);
    const [overviewAssignmentView, setOverviewAssignmentView] = useState<OverviewAssignmentView>('members');
    const [rateHistoryRefreshKey, setRateHistoryRefreshKey] = useState(0);

    const bumpRateHistory = useCallback(() => setRateHistoryRefreshKey(k => k + 1), []);

    const handleCloseDrawer = useCallback(() => {
        setIsAssignMemberDrawerOpen(false);
        setEditingMember(null);
    }, []);

    const onAssigneeMutationSuccess = useCallback(() => {
        dispatch(invalidateSingleProjectCache(project.id));
        dispatch(invalidateProjectMetrics(project.id));
        dispatch(invalidateTimesheetCache(project.id));
        dispatch(invalidateActivityLogsCache(project.id));
        bumpRateHistory();
        handleCloseDrawer();
        onRefresh?.();
    }, [dispatch, project.id, bumpRateHistory, handleCloseDrawer, onRefresh]);

    const organizationId = useOrganizationId();
    const projectAssignees = useSelector((s: RootState) => s.projectAssignees.projectAssignees[project.id]);
    const authUser = useSelector(selectAuth);
    const organizationMembers = useSelector(selectOrganizationMembers);

    const { exec: fetchOrgMembers } = useHttpGetMembersOMX(organizationId);

    const canViewMetrics = showMetrics ?? checkIfAdminOrOwner(authUser.role.name);
    const { roles, exec: fetchProjectRoles } = useHttpGetProjectRoles(false);
    const { exec: execAddAssignees } = useHttpPostProjectAssignees();
    const { exec: execPatchAssignee } = useHttpPatchProjectAssignee();
    const { execDelete: execDeleteAssignee } = useHttpDeleteProjectAssignee();
    const { data: hoursSummary } = useHttpGetProjectHoursSummary(project.id, canViewMetrics);
    const { data: earningsData, isReady: isEarningsReady } = useHttpGetProjectTotalEarnings(project.id, canViewMetrics);
    const { rows: assignmentHistoryRows } = useHttpGetProjectAssigneeAssignmentHistory(
        project.id,
        canEdit || showRates,
        project.startDate,
        rateHistoryRefreshKey
    );

    const currentMonth = DateTime.now();
    const month = currentMonth.toFormat('MMM');
    const year = currentMonth.year;

    const { currentMonthHours, totalLoggedHours } = useHttpGetUserTimeLogs(
        showOnlyOwnData ? { month, year, projectId: project.id } : { month: '', year: 0, projectId: '' }
    );

    useEffect(() => {
        if (project.projectAssignees?.length) {
            dispatch(setProjectAssignees({ projectId: project.id, assignees: project.projectAssignees }));
        }
    }, [project.projectAssignees, project.id, dispatch]);

    useEffect(() => {
        if (isAssignMemberDrawerOpen && canEdit) {
            if (organizationMembers.length === 0) {
                fetchOrgMembers();
            }
            if (roles.length === 0) {
                fetchProjectRoles();
            }
        }
    }, [isAssignMemberDrawerOpen, canEdit, organizationMembers.length, roles.length, fetchOrgMembers, fetchProjectRoles]);

    useEffect(() => {
        if (overviewAssignmentView === 'rateHistory' && organizationMembers.length === 0) {
            fetchOrgMembers();
        }
    }, [overviewAssignmentView, organizationMembers.length, fetchOrgMembers]);

    const assignees = projectAssignees ?? project.projectAssignees ?? [];

    const activeMembers = useMemo(() => {
        return assignees.filter(a => a.active);
    }, [assignees]);

    const availableMembers = useMemo(() => {
        const previouslyAssignedSubs = new Set(assignmentHistoryRows.map(row => row.userSub));
        const latestRestartDateByUserSub = assignmentHistoryRows.reduce<Record<string, string>>((acc, row) => {
            if (!row.effectiveTo) return acc;
            const nextAvailableDate = DateTime.fromISO(row.effectiveTo, { zone: 'utc' }).plus({ days: 1 }).toISODate();
            if (!nextAvailableDate) return acc;
            if (!acc[row.userSub] || nextAvailableDate > acc[row.userSub]) {
                acc[row.userSub] = nextAvailableDate;
            }
            return acc;
        }, {});
        const fallbackStartDate = project.startDate ? project.startDate.slice(0, 10) : undefined;

        return organizationMembers.map(m => ({
            label: `${m.givenName} ${m.familyName}${previouslyAssignedSubs.has(m.userSub) ? ` ${t('projects.previouslyAssignedSuffix')}` : ''}`,
            value: m.userSub,
            imageUrl: m.imageUrl,
            jobTitle: m.title,
            roleName: m.role?.name as RoleTypeEnum | undefined,
            wasPreviouslyAssigned: previouslyAssignedSubs.has(m.userSub),
            suggestedStartDate: latestRestartDateByUserSub[m.userSub] || fallbackStartDate,
            minimumStartDate: latestRestartDateByUserSub[m.userSub] || fallbackStartDate,
        }));
    }, [organizationMembers, assignmentHistoryRows, project.startDate, t]);

    const assignedUserSubs = useMemo(() => new Set(activeMembers.map(a => a.userSub)), [activeMembers]);

    const unassignedMembers = useMemo(() => {
        return availableMembers.filter(m => !assignedUserSubs.has(m.value));
    }, [availableMembers, assignedUserSubs]);

    const handleEditMember = useCallback((member: ProjectAssigneesWithUserInfoDto) => {
        setEditingMember(member);
        setIsAssignMemberDrawerOpen(true);
    }, []);

    const handleOpenAddMemberDrawer = useCallback(() => {
        setIsAssignMemberDrawerOpen(true);
    }, []);

    const handleUnassignMember = () => {
        if (editingMember) {
            const removedUserSub = editingMember.userSub;
            execDeleteAssignee(removedUserSub, project.id, {
                onSuccess: () => {
                    dispatch(
                        updateAssignee({
                            projectId: project.id,
                            userSub: removedUserSub,
                            updates: {
                                active: false,
                                markedAsObsolete: true,
                            },
                        })
                    );
                    onAssigneeMutationSuccess();
                },
            });
        }
    };

    const handleAssignMembers = (members: any[], opts?: AssignMembersSubmitOptions) => {
        if (editingMember && members.length === 1) {
            const m = members[0];
            const bill = m.billableType === 'billable';

            execPatchAssignee(
                project.id,
                editingMember.userSub,
                buildUpdateAssigneeBody(
                    {
                        billable: bill,
                        rate: bill ? parseFloat(m.rate) || 0 : 0,
                        rateType: bill ? (m.rateType === RateType.FIXED ? RateType.FIXED : RateType.HOURLY) : RateType.HOURLY,
                        employmentType: m.employmentType as EmploymentType,
                        currencyCode: m.currencyCode,
                        startDate: m.startDate,
                    },
                    opts
                        ? {
                              ...(opts.rateEffectiveFrom ? { rateEffectiveFrom: opts.rateEffectiveFrom } : {}),
                              ...(opts.currencyEffectiveFrom ? { currencyEffectiveFrom: opts.currencyEffectiveFrom } : {}),
                          }
                        : undefined
                ),
                {
                    onSuccess: response => {
                        dispatch(
                            updateAssignee({
                                projectId: project.id,
                                userSub: editingMember.userSub,
                                updates: {
                                    rate: response.rate,
                                    rateType: response.rateType,
                                    billable: response.billable,
                                    employmentType: response.employmentType,
                                    currencyCode: response.currencyCode,
                                    startDate: m.startDate,
                                },
                            })
                        );
                        onAssigneeMutationSuccess();
                    },
                }
            );
        } else {
            const payload: AssigneeDto[] = members.map(m => {
                const bill = m.billableType === 'billable';
                return {
                    userSub: m.memberId,
                    billable: bill,
                    rate: bill ? parseFloat(m.rate) || 0 : undefined,
                    rateType: bill ? (m.rateType === RateType.FIXED ? RateType.FIXED : RateType.HOURLY) : undefined,
                    employmentType: bill ? m.employmentType : undefined,
                    startDate: m.startDate,
                    assignedRoles: [{ projectRoleId: Number(m.role) }],
                };
            });

            execAddAssignees(
                project.id,
                { assignees: payload },
                {
                    onSuccess: onAssigneeMutationSuccess,
                }
            );
        }
    };

    const formatHours = (hours: number): string => {
        const totalHours = Math.floor(hours);
        return `${totalHours}h `;
    };

    const earningsValue = !isEarningsReady
        ? 'Calculating...'
        : earningsData
        ? `${getCurrencySymbol(currency)} ${earningsData.totalEarnings.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          })}`
        : '';

    const loggedHoursValue = hoursSummary ? `${hoursSummary.totalSubmittedHours.toLocaleString('en-US')} Hours` : '';

    return (
        <div className="project-overview-container">
            <div className="project-overview">
                {canViewMetrics && (
                    <div className="project-overview-tab__metrics">
                        <MetricCard
                            icon="/svg/dollar.svg"
                            title="Total Earnings"
                            value={earningsValue}
                            subduedValue={!isEarningsReady}
                            // change={earningsData?.percentageDifference}
                        />
                        <MetricCard
                            icon="/svg/clock.svg"
                            title="Total Logged Hours"
                            value={loggedHoursValue}
                            // change={
                            //     hoursSummary
                            //         ? hoursSummary.changeDirection === 'down'
                            //             ? -hoursSummary.percentageChange
                            //             : hoursSummary.changeDirection === 'up'
                            //             ? hoursSummary.percentageChange
                            //             : undefined
                            //         : undefined
                            // }
                        />
                        <MetricCard
                            icon="/svg/member.svg"
                            title="Total Members"
                            value={`${activeMembers.length} Member${activeMembers.length !== 1 ? 's' : ''}`}
                        />
                    </div>
                )}

                {showOnlyOwnData && (
                    <div className="project-overview-tab__metrics">
                        <MetricCard icon="/svg/clock.svg" title="This Month" value={formatHours(currentMonthHours)} />
                        <MetricCard icon="/svg/clock.svg" title="Total Logged Hours" value={formatHours(totalLoggedHours)} />
                        <MetricCard
                            icon="/svg/member.svg"
                            title="Team"
                            value={`${activeMembers.length} Member${activeMembers.length !== 1 ? 's' : ''}`}
                        />
                    </div>
                )}

                <div className="project-overview-tab">
                    <div className="project-overview-tab__section">
                        <div className="project-overview-tab__section-title">
                            <h2 className="heading-2">{project.name}</h2>
                            {project.endDate && (
                                <p className="paragraph-3 project-overview-tab__description">
                                    <span>Project End Date:</span>
                                    <span className="project-overview-tab__end-date">{formatDateAndTime(project.endDate, true)}</span>
                                </p>
                            )}
                        </div>

                        {project.description && <p className="paragraph-3 project-overview-tab__description">{project.description}</p>}
                    </div>

                    <OverviewAssignmentSection
                        showRates={showRates}
                        view={overviewAssignmentView}
                        onViewChange={setOverviewAssignmentView}
                        activeMembers={activeMembers}
                        assignees={assignees}
                        organizationMembers={organizationMembers}
                        canEdit={canEdit}
                        currency={currency}
                        projectId={project.id}
                        projectStartDate={project.startDate}
                        rateHistoryRefreshKey={rateHistoryRefreshKey}
                        onEditMember={handleEditMember}
                        onOpenAddMemberDrawer={handleOpenAddMemberDrawer}
                    />
                </div>

                <AssignMembersDrawer
                    open={isAssignMemberDrawerOpen}
                    onClose={handleCloseDrawer}
                    onAssign={handleAssignMembers}
                    onUnassign={handleUnassignMember}
                    availableMembers={unassignedMembers}
                    employmentTypes={EMPLOYMENT_TYPES}
                    projectRoles={roles}
                    editMode={!!editingMember}
                    editMember={editingMember || undefined}
                    currency={currency}
                    projectStartDate={project.startDate}
                />
            </div>

            <ActivityFeed projectId={project.id} clientId={project.clientId} />
        </div>
    );
};
